import * as React from 'react'
import './style/domain.scss'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { Tabs } from 'antd'
import { DomainDataForm } from '@halo/components/domain-data-form/domain-data-form'
import { noop } from '@pushly/aqe/lib/utils/noop'
import TabbedView from '@halo/components/tabbed-view/tabbed-view'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asDomainOwnedSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { DOMAINS_ROUTE } from '@halo/portals/post-auth/domains'
import { Crumb, makeCrumbClick } from '@halo/_utils/breadcrumb'
import { RouteComponentProps } from 'react-router-dom'
import { AccountDto } from '@halo/dtos/account.dto'
import { ErrorPage } from '@halo/components/error-page/error-page'
import { FeedsDataViewWrapper } from '@halo/portals/post-auth/domains/feeds/feeds-data-view-wrapper'
import { getPathEntityId } from '@halo/_utils/get-path-entity-id'

interface IProps
    extends RouteComponentProps<
        { accountId?: string; domainId: string },
        any,
        { account?: AccountDto; domain?: DomainDto }
    > {}

interface IState {
    loading: boolean
    domain?: DomainDto
}

export class Domain extends React.Component<IProps, IState> {
    protected appState: AppState
    protected appService: AppService
    protected domainService: DomainService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)

        this.state = {
            loading: !props.location.state?.domain,
            domain: props.location.state?.domain,
        }
    }

    public componentDidMount() {
        if (!this.state.domain) {
            this.loadRequestedDomain()
        }
    }

    public render() {
        const canAccessFeeds = this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asDomainOwnedSubject(SubjectEntity.FEED, this.state.domain ?? { id: this.requestedDomainId }),
        )

        const pathDomainId = getPathEntityId('domain')

        const onCrumbClick = makeCrumbClick(this.appService, () => ({
            account: this.props.location.state?.account,
        }))
        const { domain } = this.state
        const accountId = this.requestedAccountId
        let crumbs: Crumb[] | undefined
        if (domain) {
            crumbs = []
            if (accountId) {
                crumbs.push(
                    { title: 'Organizations', href: ORGANIZATIONS_ROUTE, onClick: onCrumbClick },
                    {
                        title: domain.account_name,
                        href: `${ORGANIZATIONS_ROUTE}/${accountId}`,
                        onClick: onCrumbClick,
                    },
                )
            }
            crumbs.push({
                title: 'Domains',
                href: accountId ? `${ORGANIZATIONS_ROUTE}/${accountId}#domains` : DOMAINS_ROUTE,
                onClick: onCrumbClick,
            })
            crumbs.push({ title: domain.display_name })
        }

        return (
            <div className={classnames('page', 'single-domain')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader title="Domain" onTitleSet={noop} breadcrumb={crumbs} />

                    <TabbedView
                        className={classnames(null, {
                            ['feeds-tab-hidden']: !canAccessFeeds,
                        })}
                        availablePathKeys={['feeds']}
                        defaultActiveKey="manage"
                        size="small"
                        animated={false}
                    >
                        <Tabs.TabPane key="manage" tab="Management">
                            <DomainDataForm domainId={this.requestedDomainId} />
                        </Tabs.TabPane>

                        <Tabs.TabPane key="feeds" tab="Feeds" disabled={!canAccessFeeds}>
                            {!canAccessFeeds ? (
                                <ErrorPage errorCode={403} />
                            ) : (
                                this.state.domain && (
                                    <FeedsDataViewWrapper domain={this.state.domain} appState={this.appState} />
                                )
                            )}
                        </Tabs.TabPane>
                    </TabbedView>
                </div>
            </div>
        )
    }

    protected get requestedDomainId(): number {
        return parseInt(this.props.match.params.domainId, 0)
    }

    protected get requestedAccountId(): number {
        return parseInt(this.props.match.params.accountId, 0)
    }

    protected async loadRequestedDomain() {
        this.setState({ loading: true })

        try {
            const { ok, data } = await this.domainService.fetchById(this.requestedDomainId)
            if (ok) {
                this.setState({ domain: data })
            }
        } catch {}

        this.setState({ loading: false })
    }
}
